import type { UseQueryResult } from '@tanstack/react-query';
import type { UserRouterOutput } from '~/server/routers/types';
import type { Control, FieldErrors, FieldValues, Path } from 'react-hook-form';
import { useError } from '~/hooks/useError';
import { BetterSelectInput, ControlledBetterSelectInput } from '../BetterSelectInput';

type BaseBusinessUserSelectProps = {
	getBusinessUserOpts: UseQueryResult<UserRouterOutput['getBusinessUserOpts']>;
	id: string;
	ignoreUserIds?: number[];
	labelText?: string;
	placeholder?: string;
	testId?: string;
	isFullWidth?: boolean;
};

type ControlledBusinessUserSelectProps<TFieldValues extends FieldValues> = BaseBusinessUserSelectProps & {
	control: Control<TFieldValues>;
	errors?: FieldErrors<TFieldValues>;
	name: Path<TFieldValues>;
	canBeNull?: boolean;
};

type UncontrolledBusinessUserSelectProps = BaseBusinessUserSelectProps & {
	defaultValue?: number | null;
	onChange?: (userId: number | null) => void;
};

export const UncontrolledBusinessUserSelect = ({
	defaultValue,
	getBusinessUserOpts,
	ignoreUserIds,
	id,
	labelText,
	onChange,
	placeholder,
	testId,
	isFullWidth,
}: UncontrolledBusinessUserSelectProps) => {
	const allUsers = getBusinessUserOpts.data ?? [];
	const options = ignoreUserIds ? allUsers.filter((opt) => ignoreUserIds.indexOf(opt.value) == -1) : allUsers;

	return (
		<BetterSelectInput<number>
			canBeNull
			defaultValue={defaultValue ?? null}
			id={id}
			isLoading={getBusinessUserOpts.isLoading}
			labelText={labelText}
			onChange={onChange}
			options={options}
			placeholder={placeholder}
			testId={testId}
			isFullWidth={isFullWidth}
		/>
	);
};

export const BusinessUserSelect = <TFieldValues extends FieldValues>({
	control,
	errors,
	getBusinessUserOpts,
	id,
	ignoreUserIds,
	labelText,
	name,
	placeholder,
	testId,
	isFullWidth,
	canBeNull,
}: ControlledBusinessUserSelectProps<TFieldValues>) => {
	const error = useError(name, errors ?? {});
	const allUsers = getBusinessUserOpts.data ?? [];
	const options = ignoreUserIds ? allUsers.filter((opt) => ignoreUserIds.indexOf(opt.value) == -1) : allUsers;

	return (
		<ControlledBetterSelectInput
			canBeNull={canBeNull}
			control={control}
			errorMessage={error?.message}
			id={id}
			isLoading={getBusinessUserOpts.isLoading}
			labelText={labelText}
			name={name}
			options={options}
			placeholder={placeholder}
			testId={testId}
			isFullWidth={isFullWidth}
		/>
	);
};
